import { Modal, Button, Table, Empty, ConfigProvider, Select } from "antd";
import React, { MouseEvent, useContext, useEffect, useCallback } from 'react';
import moment from "moment";
import { CheckCircleOutlined, CloseCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { AppContext } from '../../../context/AppContext';
import { Plus } from 'react-feather';
import { getBatchError } from "../../../utils/utils"; 


const MAX_FILE_SIZE = 9989622

interface Props {
    onOkCallback: (e: MouseEvent<HTMLElement>) => void;
    open: boolean;
    onClose: (e?: MouseEvent<HTMLElement>) => void;
}

interface GetRecentUploadsResponse {
    filename: string;
    id: string;
    partner_id: string;
    status: string;
    created_at: string;
    code: string;
    user_id: string
}

interface UploadsDataTable {
    dateAndTime: string;
    fileName: string;
    status: string;
}

export const OperationsUploadAwardsFile: React.FC<Props> = ({ open, onOkCallback, onClose }) => {
    const context = useContext(AppContext);
    const { onUploadOperationsAwardFile, onGetOperationsRecentUploads, onGetListOfPartners } = context;
    const [loading, setLoading] = React.useState(false)
    const [fileName, setFileName] = React.useState("")
    const [hasFileSelected, setHasFileSelected] = React.useState(false)
    const [uploadFailed, setUploadFailed] = React.useState(false)
    const [uploadStatus, setUploadStatus] = React.useState({ title: "", message: "" })
    const [tableData, setTableData] = React.useState<UploadsDataTable[]>([]);
    const [tableLoading, setTableLoading] = React.useState(false)
    const [selectedPartner, setSelectedPartner] = React.useState<any | undefined>(undefined);
    const [partnerError, setPartnerError] = React.useState<string | null>(null);
    const [partnersList, setPartnersList] = React.useState<any[]>([]);

    const refreshIcon = require('../../../assets/icons/refresh.png')
    
    const locale = {
        emptyText: <Empty />,
    };

    const buttonStyles = {
        padding: '5px',
        width: '120px',
        borderRadius: '5px',
        border: '1px solid',
    };

    const getStatusStyle = (status: any) => {
        switch (status) {
            case 'COMPLETED':
                return {
                    ...buttonStyles,
                    borderColor: '#74CA4C',
                    backgroundColor: '#E7F5E4',
                    color: '#4EC33B',
                };
            case 'PROCESSING':
                return {
                    ...buttonStyles,
                    borderColor: '#A5CFFF',
                    backgroundColor: '#F2F8FD',
                    color: '#3995FF',
                };
            case 'FAILED':
                return {
                    ...buttonStyles,
                    borderColor: '#F4B8B4',
                    backgroundColor: '#FDF2F2',
                    color: '#E6645B',
                };
            default:
                return buttonStyles;
        }
    };

    const columns = [
        {
            title: 'Date & Time',
            dataIndex: 'dateAndTime',
            key: 'dateAndTime',
            render: (text: any, record: any) => {
                if (!text) {
                    return null;
                }
                const formattedDate = moment(text).format('YYYY-MM-DD');
                const formattedTime = moment(text).format('HH:mm:ss');

                return (
                    <div>
                        <span className="text-neutralGray-50">{formattedDate}</span>
                        <br />
                        <span className="text-neutralBlack-10">{formattedTime}</span>
                        <br />
                    </div>
                )
            },
            width: 200
        },
        {
            title: 'File Name',
            dataIndex: 'fileName',
            key: 'fileName',
            width: 500
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: any) => {
                if (!status) {
                    return null;
                }
                return (
                    <div style={getStatusStyle(status)} className="text-center" >
                        {status ? status.toUpperCase() : ''}
                    </div>
                );
            },
        }
    ]

    const handleUploadFile = async (file: File) => {
        try {
            const response = await onUploadOperationsAwardFile(selectedPartner, file)
            
            if (response?.filename && response?.filename !== "") {
                const message = {
                    title: 'The file was uploaded successfully',
                    message: ''
                }
                setUploadStatus(message);
                setUploadFailed(false);
            } else {
                setErrors(response);
            }
        } catch (e) {
            setErrors(e);
            console.error(e.response?.data || e.messsage)
        } finally {
            setHasFileSelected(false);
            setLoading(false);
            onRefreshTable();
        }
    }

    const setErrors = (response: any) => {
        const [errTitle, errMsg] = getBatchError(response);
        const message = {
            title: `Upload failed: ${response.error?.case || errTitle}`,
            message: `${response.error?.message || errMsg}`
        }
        setUploadFailed(true);
        setUploadStatus(message);
    }

    const onDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();
        const dropZone = document.getElementById('csv-drop-zone');
        dropZone.classList.add('dragover');
    };

    const onDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();
        const dropZone = document.getElementById('csv-drop-zone');
        dropZone.classList.remove('dragover');
    };

    const onDrop = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();
        const dropZone = document.getElementById('csv-drop-zone');
        dropZone.classList.remove('dragover');
        const files = event.dataTransfer.files;
        handleDroppedFiles(files);
    };

    const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const inputElement = event.target as HTMLInputElement;
        const files = inputElement.files;
        handleDroppedFiles(files);
    };

    const handleDroppedFiles = (files: FileList): void => {

        if (!selectedPartner) {
            setPartnerError("Please select a partner.");
            return;
        }

        setLoading(true);
        setHasFileSelected(true);
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            setFileName(file.name)
            if (file.type === 'text/csv') {
                if (file.size <= MAX_FILE_SIZE) {
                    setUploadFailed(false);
                    handleUploadFile(file);
                } else {
                    const message = {
                        title: "Upload failed: File size exceeds maximum 10MB limit",
                        message: "",
                    }
                    setUploadFailed(true);
                    setLoading(false);
                    setHasFileSelected(false);
                    setUploadStatus(message);
                }
            } else {
                const message = {
                    title: "Upload failed: Invalid file type",
                    message: "Please upload a file with the correct format (CSV).",
                }
                setUploadFailed(true);
                setLoading(false);
                setHasFileSelected(false);
                setUploadStatus(message);
            }
        }
    }

    const onBrowseClick = () => {
        if (!selectedPartner) {
            setPartnerError("Please select a partner.");
            return;
        }

        const fileInput = document.getElementById('file-input');
        if (fileInput) {
            fileInput.click();
        }
    };

    const cancelUpload = () => {
        setUploadStatus(null);
        setLoading(false);
        setUploadFailed(false);
        setHasFileSelected(false);
    }

    const onRefreshTable = useCallback(async () => {
        setTableLoading(true);
        try {
            const response = await onGetOperationsRecentUploads();
            const data = response.items.sort((a: any, b: any) => {
                const dateA: Date = new Date(a.created_at);
                const dateB: Date = new Date(b.created_at);

                // Subtract the dates to get the latest file.
                return dateB.getTime() - dateA.getTime();
            }).splice(0, 3);

            const updatedFileList = data.map((item: GetRecentUploadsResponse) => ({
                dateAndTime: item.created_at,
                fileName: item.filename,
                status: item.status,
            }));
            setTableData(updatedFileList);
        } catch (e) {
            setTableLoading(false);
            console.error(e.response?.data || e.message);
        } finally {
            setTimeout(() => {
                setTableLoading(false);
            }, 500);
        }
    }, [onGetOperationsRecentUploads, setTableData, setTableLoading]);

    const getListOfPartners = useCallback(async () => {

        try {
            const response = await onGetListOfPartners();

            if(response) {
                const data = response.items;

                const list = data.map((item: any) => ({
                    name: item.doing_business_as,
                    id: item.id,
                }));
                setPartnersList(list);
            } else {
                setPartnersList([]);
            }
        } catch (e) {
            console.error(e.response?.data || e.message);
        } finally {
            return;
        }
    }, [onGetListOfPartners]);;


    useEffect(() => {
        if (open) {
            setUploadStatus(null);
            onRefreshTable();
        }

        getListOfPartners()
    }, [open, onRefreshTable, getListOfPartners]);

    const onCloseModal = () => {
        cancelUpload();
        onClose();
    }

    const onDownloadCsvTemplate = async () => {
        const filename = 'award-file-template.csv';

        try {
            const response = await fetch('/templates/award_file_csv_template.csv');
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.blob();

            const blob = new Blob([data], { type: 'text/csv' });
            const anchor = document.createElement('a');
            anchor.href = URL.createObjectURL(blob);
            anchor.download = filename;
            anchor.click();
        } catch (error) {
            console.error('Error downloading CSV template:', error);
        }
    }


    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: 'FF6F0B'
                },
                components: {
                    Modal: {
                        titleFontSize: 24
                    },
                    Menu: {
                        horizontalItemHoverColor: 'red',
                        horizontalItemSelectedColor: 'red',
                        itemSelectedColor: 'red',
                        darkItemSelectedBg: 'red'
                    }
                },
            }}
        >
            <Modal
                title={`Upload Earn File`} 
                className="font-poppinsSemiBold"
                onOk={onOkCallback}
                onCancel={onCloseModal}
                open={open}
                width="1000px"
                footer={null}
            >
                <Select
                    placeholder="Select a Partner"
                    allowClear
                    className={`font-poppinsRegula w-48 ${partnerError ? 'border-red-500' : ''}`}
                    onChange={(value) => {
                        setSelectedPartner(value);
                        setPartnerError(null);
                    }}
                >
                    {partnersList.map(partner => (
                        <Select.Option key={partner.id} className="font-poppinsRegular" value={partner.id}>
                            {partner.name}
                        </Select.Option>
                    ))}
                </Select>

                {partnerError && (
                    <div className="text-red-500 mt-1 text-body5 font-light	">{partnerError}</div>
                )}
                <div
                    className="file-upload-section py-8 text-xl text-center mt-5 relative custom-border-dashed rounded-lg p-4 mx-auto"
                    style={{ maxWidth: '1000px' }}
                    id="csv-drop-zone"
                    onDragOver={(e) => onDragOver(e)}
                    onDragLeave={(e) => onDragLeave(e)}
                    onDrop={(e) => onDrop(e)}
                >
                    {
                        !hasFileSelected &&
                        <>
                            {/* <img src={uploadIcon} alt="Upload Award" /> */}
                            <svg stroke="#94a3b8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" style={{ width: '64px', height: '64px' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                            </svg>
                            <span className="text-body2 my-4 text-neutralBlack">Drag and drop your file here or click on “Browse Files”.</span>
                            <input
                                type="file"
                                accept=".csv"
                                style={{ display: 'none' }}
                                onChange={(e) => handleFileSelection(e)}
                                id="file-input"
                            />
                            <Button
                                className="bg-primary font-poppinsRegular text-xs leading-6 border-primary hover:bg-primary-hovered hover:text-neutral-50 active:border-primary-border active:bg-primary-clicked"
                                style={{ alignItems: 'center', display: 'flex', height: 40, background: '#CD2628', color: 'white', marginRight: 8 }}
                                onClick={onBrowseClick}
                            >
                                <Plus size={20} className="h-4 mr-1 -ml-1" />
                                Browse Files
                            </Button>
                            <span className="text-body2 font-normal text-neutralGray-45 mt-2">Max file size: 10MB</span>
                            {
                                uploadStatus && (
                                    <div className={uploadFailed ? 'error-upload-message' : 'success-upload-message'}>
                                        {uploadFailed ? (
                                            <CloseCircleOutlined className="status-icon" />
                                        ) : (
                                            <CheckCircleOutlined className="status-icon" />
                                        )}
                                        {uploadStatus.title}
                                        <br />
                                        <span className="text-neutralBlack-75">
                                            {uploadStatus.message}
                                        </span>
                                    </div>
                                )
                            }
                        </>
                    }

                    {
                        loading && (
                            <div className="text-body3 left">
                                <div className="text-neutralBlack-50 text-left mb-2">Your file is uploading...</div>
                                <div className="loading-container">
                                    <div className="text-highlight pl-4 font-bold truncate">
                                        {fileName}
                                        <CloseOutlined className="text-neutralBlack-50 cancel-icon cursor-pointer" onClick={cancelUpload} />
                                    </div>
                                    <div className="custom-progress mt-2">
                                        <div className="indeterminate-progress"></div>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </div>
                <div className="mt-8 rounded-md font-poppinsRegular">
                    <div className="flex justify-between items-center">
                        <div className="text-base text-neutralGray-50 mb-4">
                            <span className="font-poppinsSemiBold">Recent Uploads <img onClick={onRefreshTable} alt="Refresh List" className="inline-block cursor-pointer" src={refreshIcon} /></span>
                        </div>
                        <div className="font-bold text-base text-secondary mb-4 ml-4" onClick={onDownloadCsvTemplate}>
                            <span className="px-4 cursor-pointer text-highlight hover:underline active:bg-highlight-hovered active:no-underline active:rounded hover:border-highlight hover:text-highlight h-10 text-xs sm:text-sm md:text-base p-2 sm:p-3 flex items-center justify-center w-full sm:w-auto font-poppinsSemiBold"
                            >Download Earn File Template</span>
                        </div>
                    </div>
                    <Table
                        columns={columns}
                        locale={locale}
                        dataSource={tableLoading ? [{}] : tableData}
                        loading={tableLoading}
                        pagination={false}
                        style={{ height: '278px' }}
                    >
                    </Table>
                </div>

            </Modal >
        </ConfigProvider>
    );
};